import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, XCircle, HelpCircle, AlertTriangle, ClipboardPaste, ShieldCheck } from 'lucide-react'

/* ─── Demo data ─── */
const CLICKS_DATA = [
  { row: 2, offerId: 'offer_123', offerName: 'InsureMe Now', date: '2024-01-15', clicks: 210 },
  { row: 3, offerId: 'offer_101', offerName: 'CreditCards Plus', date: '2024-01-15', clicks: 340 },
  { row: 4, offerId: 'offer_123', offerName: 'InsureMe Now', date: '2024-01-16', clicks: 195 },
  { row: 5, offerId: 'offer_101', offerName: 'CreditCards Plus', date: '2024-01-16', clicks: 420 },
  { row: 6, offerId: 'offer_102', offerName: 'HomeLoans Direct', date: '2024-01-16', clicks: 180 },
]

const CONVERSIONS_DATA = [
  { row: 2, offerId: 'offer_123', date: '2024-01-15', conversions: 8 },
  { row: 3, offerId: 'offer_101', date: '2024-01-15', conversions: 12 },
  { row: 4, offerId: 'offer_123', date: '2024-01-16', conversions: 6 },
  { row: 5, offerId: 'offer_101', date: '2024-01-16', conversions: 15 },
]

const COMBINED_DATA = CLICKS_DATA.map((c) => {
  const conv = CONVERSIONS_DATA.find((v) => v.offerId === c.offerId && v.date === c.date)
  return {
    ...c,
    conversions: conv ? conv.conversions : null,
    key: c.offerId + '|' + c.date,
  }
})

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'You have a Clicks table and a Conversions table. Which should be your base table for combining?',
    options: [
      'Always the Conversions table because it has fewer columns',
      'The table that has all the row combinations you care about',
      'It does not matter — pick either one',
      'Whichever table has fewer rows',
    ],
    correctIndex: 1,
    explanation:
      'Start with the table that has all the rows you need. If you want every Offer + Date combo that got clicks (even if there were zero conversions), use Clicks as the base.',
  },
  {
    question: 'Why do we concatenate Offer ID and Date into a single key like offer_123|2024-01-15?',
    options: [
      'It makes the spreadsheet load faster',
      'Because VLOOKUP can only search one column, so the two-field key must be combined into one value',
      'Google Sheets requires composite keys for all lookups',
      'Concatenation automatically removes duplicates',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP searches a single column. Since the unique key is two fields (Offer ID + Date), you must concatenate them into one value so VLOOKUP has a single column to search.',
  },
  {
    question: 'Why use a separator like "|" in the concatenated key instead of just joining the values directly?',
    options: [
      'Google Sheets requires a separator character',
      'It makes the formula shorter',
      'To prevent false matches — "offer_1" + "23" would equal "offer_123" without a separator',
      'It improves lookup performance',
    ],
    correctIndex: 2,
    explanation:
      'Without a separator, "offer_1" + "23" and "offer_12" + "3" both produce "offer_123". A separator like "|" makes them "offer_1|23" and "offer_12|3", preventing false matches.',
  },
  {
    question: 'In this VLOOKUP: =VLOOKUP(C2&"|"&A2, {ConvKeys, ConvData!C:C}, 2, FALSE) — what does the 2 mean?',
    options: [
      'Search the second row',
      'Return the value from column 2 of the lookup array',
      'Use the second sheet',
      'Match at least 2 characters',
    ],
    correctIndex: 1,
    explanation:
      'The 2 is the column index in the lookup array. Column 1 is the concatenated key, column 2 is the conversions values. VLOOKUP returns from column 2.',
  },
  {
    question: 'What is the main advantage of INDEX/MATCH over VLOOKUP for combining data?',
    options: [
      'INDEX/MATCH is always faster',
      'INDEX/MATCH can look in any direction and does not require the key in the first column',
      'INDEX/MATCH automatically handles errors',
      'INDEX/MATCH works with more than two tables',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP only searches the first column of the lookup range. INDEX/MATCH is more flexible — the lookup column and return column can be anywhere, and they do not need to be adjacent.',
  },
  {
    question: 'What does MATCH(C2&"|"&A2, ARRAYFORMULA(ConvData!B:B&"|"&ConvData!A:A), 0) return?',
    options: [
      'The conversions value for that row',
      'The row number (position) where the key was found',
      'TRUE or FALSE if the key exists',
      'The concatenated key string',
    ],
    correctIndex: 1,
    explanation:
      'MATCH returns a position — the row number where it found the matching key. INDEX then uses that position to grab the actual value from the conversions column.',
  },
  {
    question: 'A row exists in your Clicks table but not in Conversions. What happens when VLOOKUP tries to find it?',
    options: [
      'It returns 0',
      'It returns an empty cell',
      'It returns #N/A',
      'It skips that row automatically',
    ],
    correctIndex: 2,
    explanation:
      'When VLOOKUP cannot find a match, it returns a #N/A error. This is expected when data exists in one table but not the other.',
  },
  {
    question: 'How do you handle the #N/A error from a failed lookup?',
    options: [
      'Delete the row from your data',
      'Use =IFERROR(VLOOKUP(...), 0) to return 0 instead of #N/A',
      'Change the last VLOOKUP argument to TRUE',
      'Add the missing row to the other table',
    ],
    correctIndex: 1,
    explanation:
      'IFERROR wraps the lookup and provides a fallback value (like 0) when the lookup fails. This is the standard way to handle mismatches between tables.',
  },
  {
    question: 'You use the QUERY approach: =QUERY(ClicksData, "SELECT * WHERE ..."). How would you add the conversions column?',
    options: [
      'Add another SELECT clause to the same QUERY',
      'Use { } array notation to combine the QUERY result with a VLOOKUP/INDEX-MATCH column',
      'QUERY can join tables automatically like SQL JOIN',
      'You cannot add columns with QUERY',
    ],
    correctIndex: 1,
    explanation:
      'Google Sheets QUERY does not support JOIN. Instead, you use { } array notation to horizontally combine your QUERY output with an extra column built using VLOOKUP or INDEX/MATCH.',
  },
  {
    question: 'What does the FALSE argument at the end of VLOOKUP mean?',
    options: [
      'Return FALSE if no match is found',
      'Search from bottom to top',
      'Require an exact match (not approximate)',
      'Exclude the header row',
    ],
    correctIndex: 2,
    explanation:
      'FALSE means exact match. For combining datasets you always want exact match — an approximate match would return wrong data from a different row.',
  },
  {
    question: 'Which formula correctly pulls conversions into the Clicks table using INDEX/MATCH with a composite key?',
    options: [
      '=INDEX(ConvData!C:C, MATCH(C2&"|"&A2, ARRAYFORMULA(ConvData!B:B&"|"&ConvData!A:A), 0))',
      '=MATCH(ConvData!C:C, INDEX(C2&"|"&A2, ConvData!B:B&"|"&ConvData!A:A, 0))',
      '=VLOOKUP(INDEX(C2, A2), MATCH(ConvData!B:B, ConvData!A:A), 0)',
      '=INDEX(MATCH(C2&A2, ConvData!B:B&ConvData!A:A), ConvData!C:C, 0)',
    ],
    correctIndex: 0,
    explanation:
      'INDEX(return_range, MATCH(lookup_value, lookup_range, 0)). MATCH finds the position of the composite key, INDEX uses that position to grab the conversions value.',
  },
  {
    question: 'You have 5 rows in Clicks and 4 in Conversions (one offer/date combo has no conversions). After combining with IFERROR, how many rows does your combined table have?',
    options: [
      '4 rows (only matching rows)',
      '5 rows (all clicks rows, with 0 for the missing conversion)',
      '9 rows (all rows from both tables)',
      '1 row (only the mismatched row)',
    ],
    correctIndex: 1,
    explanation:
      'The Clicks table is the base, so you keep all 5 rows. The lookup pulls conversions for matching rows. For the one row with no match, IFERROR returns 0. You end up with 5 rows.',
  },
  {
    question: 'A colleague typed "210" directly into a cell instead of using a formula. What is the main problem with this?',
    options: [
      'The number takes up more storage space',
      'Google Sheets will flag it as an error',
      'There is no traceability — you cannot verify where the number came from or whether it is correct',
      'Manually entered numbers are always wrong',
    ],
    correctIndex: 2,
    explanation:
      'When you type a value directly, there is no link back to the source. A formula like =VLOOKUP(...) lets anyone click the cell and trace exactly where the data came from.',
  },
  {
    question: 'What does "Paste as Values" (Ctrl+Shift+V) do to a cell that contains =VLOOKUP(...)?',
    options: [
      'It deletes the cell contents entirely',
      'It replaces the formula with the static result — the number stays but the formula is gone',
      'It copies the formula to another cell',
      'It converts the formula to a comment',
    ],
    correctIndex: 1,
    explanation:
      'Paste as Values strips the formula and keeps only the calculated result. The cell now shows a plain number (e.g., 210) with no formula behind it. This is how you "freeze" data for historical records.',
  },
  {
    question: 'Why should you freeze formula-driven reports at month end by pasting as values?',
    options: [
      'Formulas slow down the spreadsheet over time',
      'Google Sheets deletes old formula data automatically',
      'If the source data changes later (e.g., a duplicate is removed), your reported numbers would silently change',
      'Paste as Values makes the data more accurate',
    ],
    correctIndex: 2,
    explanation:
      'Formulas always reflect the current source data. If upstream data changes after you have reported a number, your report changes too — without anyone noticing. Freezing with Paste as Values locks in the numbers you actually reported.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Small table component ─── */
function MiniTable({ title, color, headers, rows, highlightRow = null, highlightCol = null, showKeys = false }) {
  const borderColor = `border-${color}-200`
  const headerBg = `bg-${color}-50`
  const headerText = `text-${color}-700`
  const titleBg = `bg-${color}-600`

  return (
    <div className="overflow-hidden rounded-lg border shadow-sm" style={{ borderColor: `var(--color-${color}-200, #e2e8f0)` }}>
      {title && (
        <div className={`${titleBg} text-white text-center py-1.5 text-xs font-bold`}>
          {title}
        </div>
      )}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className={headerBg}>
            {headers.map((h, i) => (
              <th
                key={i}
                className={`border-b ${borderColor} px-2 py-1.5 text-xs ${headerText} font-medium ${i < headers.length - 1 ? `border-r ${borderColor}` : ''}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`transition-colors ${highlightRow === i ? `bg-${color}-100` : ''}`}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`border-b border-${color}-100 px-2 py-1.5 text-xs ${
                    j < row.length - 1 ? `border-r border-${color}-100` : ''
                  } ${highlightRow === i && highlightCol === j ? 'font-bold bg-amber-100 text-amber-800' : ''} ${
                    j === 0 ? 'font-mono text-[11px]' : ''
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Section 0: Why Formulas, Not Manual Entry ─── */
function WhyFormulasSection() {
  const [showAnswer, setShowAnswer] = useState(false)

  const manualRows = [
    ['offer_123', '2024-01-15', '210'],
    ['offer_101', '2024-01-15', '340'],
    ['offer_123', '2024-01-16', '159'],
    ['offer_101', '2024-01-16', '420'],
  ]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Why Formulas, Not Manual Entry</h2>
        <p className="text-lg text-gray-600">
          Before learning the formulas, let us understand why they matter.
          Typing numbers by hand feels faster — but it creates invisible problems.
        </p>
      </div>

      {/* Spot the error challenge */}
      <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm font-bold text-amber-800">Challenge: Can you spot the error?</p>
        </div>
        <p className="text-sm text-amber-700">
          Someone manually typed these click counts into a report. One cell is wrong.
        </p>
        <div className="overflow-hidden rounded-lg border border-amber-200 shadow-sm">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-amber-100">
                <th className="border-b border-r border-amber-200 px-3 py-2 text-xs text-amber-700 font-medium">Offer ID</th>
                <th className="border-b border-r border-amber-200 px-3 py-2 text-xs text-amber-700 font-medium">Date</th>
                <th className="border-b border-amber-200 px-3 py-2 text-xs text-amber-700 font-medium">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {manualRows.map((row, i) => (
                <tr key={i} className={showAnswer && i === 2 ? 'bg-red-100' : ''}>
                  <td className="border-b border-r border-amber-100 px-3 py-2 font-mono text-xs">{row[0]}</td>
                  <td className="border-b border-r border-amber-100 px-3 py-2 text-xs">{row[1]}</td>
                  <td className={`border-b border-amber-100 px-3 py-2 text-xs font-semibold ${
                    showAnswer && i === 2 ? 'text-red-700 line-through' : ''
                  }`}>
                    {row[2]}
                    {showAnswer && i === 2 && (
                      <span className="ml-2 text-emerald-700 no-underline" style={{ textDecoration: 'none' }}>
                        should be 195
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          onClick={() => setShowAnswer(!showAnswer)}
          className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
            showAnswer
              ? 'border-red-300 bg-red-100 text-red-700'
              : 'border-amber-300 bg-amber-100 text-amber-700 hover:bg-amber-200'
          }`}
        >
          {showAnswer ? 'Row 3: 159 should be 195 (transposed digits)' : 'Reveal the error'}
        </button>
        {showAnswer && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-amber-700"
          >
            This is a real pattern. A single mistyped digit can throw off an entire report,
            and nobody notices because the cell just shows a number — there is nothing to audit.
          </motion.p>
        )}
      </div>

      {/* Side-by-side comparison */}
      <div className="grid gap-4 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="rounded-lg border-2 border-red-200 bg-red-50 p-5 space-y-4"
        >
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <h3 className="font-bold text-red-800">Manual Entry</h3>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg bg-white border border-red-200 p-3">
              <p className="text-xs font-bold text-red-700 mb-1">What the cell shows:</p>
              <div className="rounded bg-gray-100 px-3 py-2 font-mono text-lg text-gray-900 text-center">
                210
              </div>
              <p className="text-xs text-red-600 mt-2">
                Where did this come from? Who typed it? Is it correct?
                There is no way to tell.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">
                  <strong>Human error</strong> — typos, copy-paste into the wrong row, transposed digits
                </p>
              </div>
              <div className="flex items-start gap-2">
                <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">
                  <strong>No traceability</strong> — if the number looks wrong next week, you cannot verify it
                </p>
              </div>
              <div className="flex items-start gap-2">
                <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">
                  <strong>Stale data</strong> — new data arrives tomorrow, but your manually typed numbers do not update
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-5 space-y-4"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <h3 className="font-bold text-emerald-800">Formula</h3>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg bg-white border border-emerald-200 p-3">
              <p className="text-xs font-bold text-emerald-700 mb-1">What the cell shows:</p>
              <div className="rounded bg-gray-100 px-3 py-2 font-mono text-lg text-gray-900 text-center">
                210
              </div>
              <p className="text-xs font-bold text-emerald-700 mt-2 mb-1">Click the cell to see:</p>
              <div className="rounded bg-gray-900 px-3 py-2 font-mono text-sm text-emerald-400 text-center">
                =VLOOKUP(C2&amp;&quot;|&quot;&amp;A2, ...)
              </div>
              <p className="text-xs text-emerald-600 mt-2">
                You can trace exactly where the number comes from. Click it,
                read the formula, verify the source.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700">
                  <strong>No typos</strong> — the formula pulls the exact value from the source
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700">
                  <strong>Full traceability</strong> — anyone can click the cell and see the logic
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700">
                  <strong>Auto-updates</strong> — when source data changes, the formula recalculates
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>The rule:</strong> If a number comes from another sheet or source, use a formula to pull it in.
          Save manual entry for truly one-off notes or labels — never for data you need to trust.
        </p>
      </div>
    </div>
  )
}

/* ─── Section 1: The Problem — Side by side tables ─── */
function ProblemSection() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">The Problem: Two Separate Tables</h2>
        <p className="text-lg text-gray-600">
          Clicks and conversions live in different sources. You need them in one table
          to calculate conversion rates and build reports.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <MiniTable
            title="Clicks Sheet"
            color="blue"
            headers={['Offer ID', 'Offer Name', 'Date', 'Clicks']}
            rows={CLICKS_DATA.map((r) => [r.offerId, r.offerName, r.date, r.clicks])}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <MiniTable
            title="Conversions Sheet"
            color="green"
            headers={['Offer ID', 'Date', 'Conversions']}
            rows={CONVERSIONS_DATA.map((r) => [r.offerId, r.date, r.conversions])}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="flex justify-center"
      >
        <div className="rounded-lg bg-gray-100 px-4 py-2 text-gray-400 text-2xl">&#8595;</div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        <MiniTable
          title="What We Want: Combined Table"
          color="emerald"
          headers={['Offer ID', 'Offer Name', 'Date', 'Clicks', 'Conversions', 'CR%']}
          rows={COMBINED_DATA.map((r) => [
            r.offerId,
            r.offerName,
            r.date,
            r.clicks,
            r.conversions !== null ? r.conversions : '(lookup needed)',
            r.conversions !== null ? ((r.conversions / r.clicks) * 100).toFixed(1) + '%' : '--',
          ])}
        />
      </motion.div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>The plan:</strong> Use the Clicks table as the base (it has all the rows we care about),
          then use lookup formulas to pull conversions from the Conversions sheet into a new column.
          The last row (HomeLoans Direct, Jan 16) has no conversions data — we will handle that too.
        </p>
      </div>
    </div>
  )
}

/* ─── Section 2: VLOOKUP with concatenated key ─── */
function VlookupDemo() {
  const [selectedRow, setSelectedRow] = useState(0)
  const row = CLICKS_DATA[selectedRow]
  const key = row.offerId + '|' + row.date
  const matchIdx = CONVERSIONS_DATA.findIndex((c) => c.offerId === row.offerId && c.date === row.date)
  const matchRow = matchIdx !== -1 ? CONVERSIONS_DATA[matchIdx] : null

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Click a row in the Clicks table to trace the VLOOKUP through the data.
      </p>

      {/* Row selector */}
      <div className="flex flex-wrap gap-2">
        {CLICKS_DATA.map((r, i) => (
          <button
            key={i}
            onClick={() => setSelectedRow(i)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedRow === i
                ? 'border-blue-300 bg-blue-100 text-blue-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            Row {r.row}: {r.offerId}, {r.date}
          </button>
        ))}
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500 font-mono">fx</span>
          <div className="w-px h-3 bg-gray-700" />
        </div>
        <pre className="font-mono text-sm whitespace-pre-wrap">
          <span className="text-gray-500">=VLOOKUP(</span>
          <span className="text-amber-400">C{row.row}&amp;&quot;|&quot;&amp;A{row.row}</span>
          <span className="text-gray-500">, {'{'} </span>
          <span className="text-green-400">ConvData!B:B&amp;&quot;|&quot;&amp;ConvData!A:A</span>
          <span className="text-gray-500">, </span>
          <span className="text-blue-400">ConvData!C:C</span>
          <span className="text-gray-500"> {'}'}, </span>
          <span className="text-pink-400">2</span>
          <span className="text-gray-500">, </span>
          <span className="text-red-400">FALSE</span>
          <span className="text-gray-500">)</span>
        </pre>
      </div>

      {/* Step-by-step trace */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedRow}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.2 }}
          className="space-y-3"
        >
          {/* Step 1: Build the key */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-bold text-amber-700">Step 1: Build the lookup key</p>
            <p className="text-sm text-amber-800 mt-1 font-mono">
              C{row.row}&amp;&quot;|&quot;&amp;A{row.row} = &quot;{row.offerId}&quot; &amp; &quot;|&quot; &amp; &quot;{row.date}&quot; = <strong>&quot;{key}&quot;</strong>
            </p>
          </div>

          {/* Step 2: Search the conversions table */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-700">Step 2: Search for &quot;{key}&quot; in Conversions</p>
            <div className="mt-2 space-y-1">
              {CONVERSIONS_DATA.map((c, i) => {
                const cKey = c.offerId + '|' + c.date
                const isMatch = i === matchIdx
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 rounded px-2 py-1 text-xs font-mono ${
                      isMatch ? 'bg-green-200 text-green-900 font-bold' : 'text-green-700'
                    }`}
                  >
                    <span className="w-32 truncate">{cKey}</span>
                    <span className={isMatch ? 'text-green-800' : 'text-green-400'}>
                      {isMatch ? '= MATCH' : '!= skip'}
                    </span>
                    {isMatch && <span className="ml-auto font-bold">Conv: {c.conversions}</span>}
                  </div>
                )
              })}
              {matchIdx === -1 && (
                <div className="flex items-center gap-2 rounded bg-red-100 px-2 py-1 text-xs text-red-700 font-bold">
                  No match found — returns #N/A
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Result */}
          <div className={`rounded-lg border p-3 ${matchRow ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <p className={`text-xs font-bold ${matchRow ? 'text-emerald-700' : 'text-red-700'}`}>Step 3: Return column 2</p>
            <p className={`text-sm mt-1 font-mono font-bold ${matchRow ? 'text-emerald-800' : 'text-red-800'}`}>
              {matchRow ? `= ${matchRow.conversions}` : '= #N/A (no match)'}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Argument breakdown */}
      <div className="mt-3 space-y-1 text-sm text-gray-600">
        <p><strong className="text-gray-800">search_key</strong> — <code className="font-mono text-xs bg-gray-100 px-1 rounded">C{row.row}&amp;&quot;|&quot;&amp;A{row.row}</code> — the composite key to find</p>
        <p><strong className="text-gray-800">range</strong> — <code className="font-mono text-xs bg-gray-100 px-1 rounded">{'{'} keys, values {'}'}</code> — virtual array: key column + data column</p>
        <p><strong className="text-gray-800">index</strong> — <code className="font-mono text-xs bg-gray-100 px-1 rounded">2</code> — return from column 2 (conversions)</p>
        <p><strong className="text-gray-800">is_sorted</strong> — <code className="font-mono text-xs bg-gray-100 px-1 rounded">FALSE</code> — exact match only</p>
      </div>
    </div>
  )
}

/* ─── Section 3: INDEX/MATCH demo ─── */
function IndexMatchDemo() {
  const [selectedRow, setSelectedRow] = useState(0)
  const row = CLICKS_DATA[selectedRow]
  const key = row.offerId + '|' + row.date
  const matchIdx = CONVERSIONS_DATA.findIndex((c) => c.offerId === row.offerId && c.date === row.date)
  const matchRow = matchIdx !== -1 ? CONVERSIONS_DATA[matchIdx] : null

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Same data, same result — but INDEX/MATCH separates the &quot;finding&quot; step from the &quot;returning&quot; step.
      </p>

      <div className="flex flex-wrap gap-2">
        {CLICKS_DATA.map((r, i) => (
          <button
            key={i}
            onClick={() => setSelectedRow(i)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedRow === i
                ? 'border-purple-300 bg-purple-100 text-purple-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            Row {r.row}: {r.offerId}, {r.date}
          </button>
        ))}
      </div>

      {/* Formula */}
      <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
        <pre className="font-mono text-sm whitespace-pre-wrap">
          <span className="text-gray-500">=</span>
          <span className="text-blue-400">INDEX</span>
          <span className="text-gray-500">(</span>
          <span className="text-blue-300">ConvData!C:C</span>
          <span className="text-gray-500">, </span>
          <span className="text-pink-400">MATCH</span>
          <span className="text-gray-500">(</span>
          <span className="text-amber-400">C{row.row}&amp;&quot;|&quot;&amp;A{row.row}</span>
          <span className="text-gray-500">, </span>
          <span className="text-green-400">ARRAYFORMULA(ConvData!B:B&amp;&quot;|&quot;&amp;ConvData!A:A)</span>
          <span className="text-gray-500">, 0))</span>
        </pre>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={selectedRow}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.2 }}
          className="space-y-3"
        >
          {/* MATCH step */}
          <div className="rounded-lg border border-pink-200 bg-pink-50 p-3">
            <p className="text-xs font-bold text-pink-700">MATCH: Find the position of &quot;{key}&quot;</p>
            <div className="mt-2 space-y-1">
              {CONVERSIONS_DATA.map((c, i) => {
                const cKey = c.offerId + '|' + c.date
                const isMatch = i === matchIdx
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 rounded px-2 py-1 text-xs font-mono ${
                      isMatch ? 'bg-pink-200 text-pink-900 font-bold' : 'text-pink-600'
                    }`}
                  >
                    <span className="text-pink-400 w-6">#{i + 1}</span>
                    <span className="flex-1 truncate">{cKey}</span>
                    {isMatch && <span className="font-bold">Position = {i + 1}</span>}
                  </div>
                )
              })}
              {matchIdx === -1 && (
                <div className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 font-bold">
                  No match — MATCH returns #N/A
                </div>
              )}
            </div>
          </div>

          {/* INDEX step */}
          <div className={`rounded-lg border p-3 ${matchRow ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'}`}>
            <p className={`text-xs font-bold ${matchRow ? 'text-blue-700' : 'text-red-700'}`}>
              INDEX: Get value at position {matchIdx !== -1 ? matchIdx + 1 : '??'} from ConvData!C:C
            </p>
            <p className={`text-sm mt-1 font-mono font-bold ${matchRow ? 'text-blue-800' : 'text-red-800'}`}>
              {matchRow ? `= ${matchRow.conversions}` : '= #N/A (MATCH failed, so INDEX fails too)'}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Comparison callout */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
          <h4 className="font-bold text-blue-800 text-sm">INDEX/MATCH advantage</h4>
          <p className="text-xs text-blue-700 mt-1">
            The lookup column and return column are specified separately.
            The key does not need to be in the first column of the range.
          </p>
        </div>
        <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
          <h4 className="font-bold text-amber-800 text-sm">VLOOKUP advantage</h4>
          <p className="text-xs text-amber-700 mt-1">
            Simpler syntax when the key is already in column 1.
            For our combining use case, both work equally well.
          </p>
        </div>
      </div>
    </div>
  )
}

/* ─── Section 4: QUERY approach ─── */
function QuerySection() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Method 3: QUERY + Array Notation</h2>
        <p className="text-lg text-gray-600">
          Google Sheets QUERY does not support JOIN like SQL. But you can combine QUERY output with
          lookup columns using <code className="rounded bg-gray-100 px-1 font-mono text-sm">{'{ }'}</code> array notation.
        </p>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">Step 1 — Pull clicks data with QUERY:</p>
          <pre className="font-mono text-sm whitespace-pre-wrap">
            <span className="text-gray-500">=QUERY(</span>
            <span className="text-blue-400">ClicksData!A:D</span>
            <span className="text-gray-500">, </span>
            <span className="text-amber-400">&quot;SELECT * ORDER BY A, C&quot;</span>
            <span className="text-gray-500">)</span>
          </pre>
        </div>

        <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">Step 2 — Add a conversions column alongside it using {'{ }'}:</p>
          <pre className="font-mono text-sm whitespace-pre-wrap">
            <span className="text-gray-500">={'{'} </span>
            <span className="text-blue-400">QUERY(ClicksData!A:D, &quot;SELECT *&quot;)</span>
            <span className="text-gray-500">, </span>
            <span className="text-green-400">ARRAYFORMULA(IFERROR(VLOOKUP(</span>{'\n'}
            <span className="text-green-400">  ClicksData!C:C&amp;&quot;|&quot;&amp;ClicksData!A:A,</span>{'\n'}
            <span className="text-green-400">  {'{'} ConvData!B:B&amp;&quot;|&quot;&amp;ConvData!A:A, ConvData!C:C {'}'},</span>{'\n'}
            <span className="text-green-400">  2, FALSE), 0))</span>
            <span className="text-gray-500"> {'}'}</span>
          </pre>
        </div>

        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-2">
          <p className="text-sm text-purple-800">
            <strong>How {'{ }'} works:</strong> Curly braces create arrays.
            A comma between items places them <strong>side by side</strong> (new columns).
            A semicolon stacks them <strong>vertically</strong> (new rows).
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded bg-purple-100 p-2">
              <code className="text-xs font-mono text-purple-800">={'{'} A, B {'}'}</code>
              <p className="text-xs text-purple-600 mt-1">A and B side by side</p>
            </div>
            <div className="rounded bg-purple-100 p-2">
              <code className="text-xs font-mono text-purple-800">={'{'} A ; B {'}'}</code>
              <p className="text-xs text-purple-600 mt-1">A stacked on top of B</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h4 className="font-bold text-amber-800 text-sm">When to use the QUERY approach</h4>
          <p className="text-sm text-amber-700 mt-1">
            When you want to sort, filter, or reshape your base data in the same step as combining.
            For simple combining, VLOOKUP or INDEX/MATCH is usually simpler.
          </p>
        </div>
      </div>
    </div>
  )
}

/* ─── Section 5: Handling mismatches ─── */
function MismatchDemo() {
  const [showIferror, setShowIferror] = useState(false)

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Row 6 (HomeLoans Direct, 2024-01-16) exists in Clicks but not in Conversions. Watch what happens:
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <p className="text-xs font-bold text-blue-600 mb-2">Clicks (5 rows)</p>
          <div className="overflow-hidden rounded-lg border border-blue-200 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border-b border-r border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Offer</th>
                  <th className="border-b border-r border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Date</th>
                  <th className="border-b border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {CLICKS_DATA.map((r, i) => {
                  const hasMatch = CONVERSIONS_DATA.some((c) => c.offerId === r.offerId && c.date === r.date)
                  return (
                    <tr key={i} className={!hasMatch ? 'bg-red-50' : ''}>
                      <td className="border-b border-r border-blue-100 px-2 py-1.5 font-mono text-[11px]">{r.offerId}</td>
                      <td className="border-b border-r border-blue-100 px-2 py-1.5 text-[11px]">{r.date}</td>
                      <td className="border-b border-blue-100 px-2 py-1.5 text-[11px] font-semibold">{r.clicks}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-red-600 mt-1">Red row has no match in Conversions</p>
        </div>

        <div>
          <p className="text-xs font-bold text-green-600 mb-2">Conversions (4 rows)</p>
          <div className="overflow-hidden rounded-lg border border-green-200 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border-b border-r border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Offer</th>
                  <th className="border-b border-r border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Date</th>
                  <th className="border-b border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Conv</th>
                </tr>
              </thead>
              <tbody>
                {CONVERSIONS_DATA.map((r, i) => (
                  <tr key={i}>
                    <td className="border-b border-r border-green-100 px-2 py-1.5 font-mono text-[11px]">{r.offerId}</td>
                    <td className="border-b border-r border-green-100 px-2 py-1.5 text-[11px]">{r.date}</td>
                    <td className="border-b border-green-100 px-2 py-1.5 text-[11px] font-semibold">{r.conversions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-green-600 mt-1">No HomeLoans Direct on Jan 16</p>
        </div>
      </div>

      {/* Toggle IFERROR */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowIferror(!showIferror)}
          className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all ${
            showIferror
              ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
              : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {showIferror ? 'IFERROR ON' : 'Without IFERROR'}
        </button>
        <span className="text-xs text-gray-500">
          {showIferror ? 'Errors handled gracefully' : 'Click to wrap in IFERROR'}
        </span>
      </div>

      {/* Formula */}
      <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
        <pre className="font-mono text-sm whitespace-pre-wrap">
          {showIferror ? (
            <>
              <span className="text-emerald-400">=IFERROR(</span>
              <span className="text-gray-500">VLOOKUP(C2&amp;&quot;|&quot;&amp;A2, {'{'} keys, convs {'}'}, 2, FALSE)</span>
              <span className="text-emerald-400">, 0)</span>
            </>
          ) : (
            <>
              <span className="text-gray-500">=VLOOKUP(C2&amp;&quot;|&quot;&amp;A2, {'{'} keys, convs {'}'}, 2, FALSE)</span>
            </>
          )}
        </pre>
      </div>

      {/* Result table */}
      <div className="overflow-hidden rounded-lg border-2 border-emerald-300 shadow-sm">
        <div className="bg-emerald-600 text-white text-center py-1.5 text-xs font-bold">Result</div>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-emerald-50">
              <th className="border-b border-r border-emerald-200 px-2 py-1.5 text-xs text-emerald-700 font-medium">Offer</th>
              <th className="border-b border-r border-emerald-200 px-2 py-1.5 text-xs text-emerald-700 font-medium">Date</th>
              <th className="border-b border-r border-emerald-200 px-2 py-1.5 text-xs text-emerald-700 font-medium">Clicks</th>
              <th className="border-b border-emerald-200 px-2 py-1.5 text-xs text-emerald-700 font-medium">Conversions</th>
            </tr>
          </thead>
          <tbody>
            {COMBINED_DATA.map((r, i) => {
              const hasError = r.conversions === null
              return (
                <tr key={i} className={hasError ? (showIferror ? '' : 'bg-red-50') : ''}>
                  <td className="border-b border-r border-emerald-100 px-2 py-1.5 font-mono text-[11px]">{r.offerId}</td>
                  <td className="border-b border-r border-emerald-100 px-2 py-1.5 text-[11px]">{r.date}</td>
                  <td className="border-b border-r border-emerald-100 px-2 py-1.5 text-[11px] font-semibold">{r.clicks}</td>
                  <td className={`border-b border-emerald-100 px-2 py-1.5 text-[11px] font-semibold ${
                    hasError && !showIferror ? 'text-red-600' : ''
                  }`}>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={showIferror ? 'fixed' : 'error'}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {hasError
                          ? (showIferror ? '0' : '#N/A')
                          : r.conversions}
                      </motion.span>
                    </AnimatePresence>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showIferror && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
        >
          <p className="text-sm text-emerald-800">
            <strong>IFERROR replaces the #N/A with 0.</strong> The row stays in your table, and downstream
            formulas (like conversion rate) work without errors.
          </p>
        </motion.div>
      )}
    </div>
  )
}

/* ─── Section 6: Locking In Historical Data ─── */
function HistoricalDataSection() {
  const [pastedAsValues, setPastedAsValues] = useState(false)
  const [sourceChanged, setSourceChanged] = useState(false)

  const originalValue = 450
  const changedValue = 448

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Locking In Historical Data</h2>
        <p className="text-lg text-gray-600">
          Formulas are great for live data. But for month-end reporting, you need to &quot;close the books&quot; —
          freeze the numbers so they do not change after you have reported them.
        </p>
      </div>

      {/* The problem */}
      <div className="rounded-lg border-2 border-red-200 bg-red-50 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="font-bold text-red-800">The Silent Change Problem</h3>
        </div>
        <div className="space-y-2 text-sm text-red-700">
          <p>
            You report &quot;450 conversions in January&quot; to a client. Your formula-driven sheet confirms it.
          </p>
          <p>
            Two weeks later, the data team removes 2 duplicate records from the source.
            Your formula quietly recalculates to 448.
          </p>
          <p className="font-bold">
            The client was told 450. Your sheet now says 448. Nobody noticed the change.
          </p>
        </div>
      </div>

      {/* Interactive demo */}
      <div className="space-y-4">
        <h3 className="font-bold text-gray-900">The Solution: Copy, Paste as Values</h3>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Before: formula cell */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-gray-500">YOUR REPORT CELL</p>
            <div className="rounded-lg border-2 border-gray-300 bg-white p-4 space-y-2">
              <div className="rounded bg-gray-100 px-3 py-2 text-center">
                <span className="font-mono text-2xl font-bold text-gray-900">
                  {sourceChanged && !pastedAsValues ? changedValue : originalValue}
                </span>
              </div>
              <div className="rounded bg-gray-900 px-3 py-1.5">
                <code className="text-xs font-mono text-emerald-400">
                  {pastedAsValues ? (
                    <span className="text-amber-400">{originalValue}</span>
                  ) : (
                    '=SUMPRODUCT(IFERROR(VLOOKUP(...), 0))'
                  )}
                </code>
              </div>
              <p className="text-xs text-gray-500 text-center">
                {pastedAsValues
                  ? 'Static value — formula is gone, number is frozen'
                  : 'Live formula — recalculates when source changes'}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500">TRY IT</p>

            <button
              onClick={() => setPastedAsValues(!pastedAsValues)}
              className={`w-full flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all ${
                pastedAsValues
                  ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                  : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ClipboardPaste className="w-4 h-4 shrink-0" />
              {pastedAsValues ? 'Values pasted — formula removed' : 'Paste as Values (Ctrl+Shift+V)'}
            </button>

            <button
              onClick={() => setSourceChanged(!sourceChanged)}
              className={`w-full flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all ${
                sourceChanged
                  ? 'border-amber-300 bg-amber-100 text-amber-700'
                  : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {sourceChanged ? 'Source changed (2 dupes removed)' : 'Simulate source data change'}
            </button>

            {sourceChanged && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className={`rounded-lg border p-3 ${
                  pastedAsValues
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <p className={`text-xs font-bold ${pastedAsValues ? 'text-emerald-700' : 'text-red-700'}`}>
                  {pastedAsValues
                    ? 'Safe! Your frozen value still shows 450. The number you reported is preserved.'
                    : 'Problem! Your formula recalculated to 448. The number you reported to the client has silently changed.'}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Workflow steps */}
      <div className="rounded-lg bg-gray-900 p-5 space-y-4">
        <p className="text-xs text-gray-400 font-bold">PASTE AS VALUES WORKFLOW</p>
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-blue-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center shrink-0">1</span>
            <p className="text-sm text-gray-300">Select the formula range you want to freeze</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-blue-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center shrink-0">2</span>
            <p className="text-sm text-gray-300">
              <span className="text-white font-mono">Ctrl+C</span> to copy
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-blue-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center shrink-0">3</span>
            <p className="text-sm text-gray-300">
              Right-click &rarr; <span className="text-white font-mono">Paste special</span> &rarr; <span className="text-white font-mono">Values only</span>
              <br />
              <span className="text-gray-500">or keyboard shortcut: </span>
              <span className="text-amber-400 font-mono">Ctrl+Shift+V</span>
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-emerald-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center shrink-0">&#10003;</span>
            <p className="text-sm text-gray-300">Formulas are replaced with static numbers — data is frozen</p>
          </div>
        </div>
      </div>

      {/* Best practice pattern */}
      <div className="rounded-lg border-2 border-purple-200 bg-purple-50 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600" />
          <h3 className="font-bold text-purple-800">Best Practice: The Live + Archive Pattern</h3>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white border border-purple-200 p-3 space-y-1">
            <span className="rounded bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5">1. LIVE TAB</span>
            <p className="text-xs text-purple-700">
              Keep a &quot;Live&quot; tab with all your formulas for the current period. This is your working sheet.
            </p>
          </div>
          <div className="rounded-lg bg-white border border-purple-200 p-3 space-y-1">
            <span className="rounded bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5">2. DUPLICATE</span>
            <p className="text-xs text-purple-700">
              At month end, right-click the tab &rarr; &quot;Duplicate&quot;. Rename to &quot;Jan 2024 (Final)&quot;.
            </p>
          </div>
          <div className="rounded-lg bg-white border border-purple-200 p-3 space-y-1">
            <span className="rounded bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5">3. FREEZE</span>
            <p className="text-xs text-purple-700">
              On the duplicate: Select All (Ctrl+A) &rarr; Copy &rarr; Paste as Values. Formulas are now static numbers.
            </p>
          </div>
          <div className="rounded-lg bg-white border border-purple-200 p-3 space-y-1">
            <span className="rounded bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5">4. PROTECT</span>
            <p className="text-xs text-purple-700">
              Data &rarr; Protect sheet. Set permissions so nobody can accidentally edit the frozen data.
            </p>
          </div>
        </div>

        <p className="text-xs text-purple-600">
          Your Live tab continues with formulas for the next period. Each month end, repeat the process.
        </p>
      </div>

      {/* Safety net callouts */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h4 className="font-bold text-blue-800 text-sm">Version history as a safety net</h4>
          <p className="text-xs text-blue-700 mt-1">
            Google Sheets keeps a full version history (File &rarr; Version history).
            Even if someone accidentally edits a frozen sheet, you can restore a previous version.
            But do not rely on this — protect the sheet first.
          </p>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h4 className="font-bold text-blue-800 text-sm">Protect ranges, not just sheets</h4>
          <p className="text-xs text-blue-700 mt-1">
            You can protect specific cell ranges instead of the entire sheet.
            Useful when some columns are frozen data but others need ongoing edits
            (e.g., a &quot;Notes&quot; column next to frozen numbers).
          </p>
        </div>
      </div>
    </div>
  )
}

/* ─── Section 7: Build it yourself exercise ─── */
const EXERCISE_STEPS = [
  {
    question: 'Your Clicks table has 200 rows and Conversions has 180 rows. Which do you use as the base?',
    options: [
      'Conversions table (fewer rows = faster)',
      'Clicks table (has all the offer+date combos we need)',
      'Create a new empty table',
    ],
    correctIndex: 1,
    explanation: 'Start with the table that has all the rows you care about. Clicks has 200 unique offer+date combos — we want all of them, even if some have no conversions.',
  },
  {
    question: 'The Clicks table has columns: A=Date, B=Offer Name, C=Offer ID, D=Clicks. What composite key do you build?',
    options: [
      '=A2&B2 (Date + Offer Name)',
      '=C2&"|"&A2 (Offer ID + Date with separator)',
      '=D2&"|"&C2 (Clicks + Offer ID)',
    ],
    correctIndex: 1,
    explanation: 'The unique key is Offer ID + Date. Use a separator "|" to prevent false matches. Offer Name is not a reliable key (it could change), and Clicks is a value, not an identifier.',
  },
  {
    question: 'The Conversions table has columns: A=Date, B=Offer ID, C=Conversions. Which formula pulls conversions into your Clicks table?',
    options: [
      '=VLOOKUP(C2&"|"&A2, {ConvData!B:B&"|"&ConvData!A:A, ConvData!C:C}, 2, FALSE)',
      '=VLOOKUP(C2, ConvData!B:B, 2, FALSE)',
      '=INDEX(ConvData!A:A, MATCH(C2, ConvData!C:C, 0))',
    ],
    correctIndex: 0,
    explanation: 'You need the composite key (Offer ID + Date) in both the search and lookup range. The second option only matches on Offer ID (ambiguous). The third returns a Date, not Conversions.',
  },
  {
    question: 'Some clicks rows have no matching conversions. What do you wrap the formula in?',
    options: [
      'ARRAYFORMULA(...)',
      'IFERROR(..., 0)',
      'IF(ISNA(...), "", ...)',
    ],
    correctIndex: 1,
    explanation: 'IFERROR(..., 0) catches the #N/A from failed lookups and replaces it with 0. Clean and simple.',
  },
]

function BuildExercise() {
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showResult, setShowResult] = useState(false)
  const [score, setScore] = useState(0)
  const [finished, setFinished] = useState(false)

  const step = EXERCISE_STEPS[currentStep]
  const isCorrect = selectedAnswer === step?.correctIndex

  const handleSelect = (index) => {
    setSelectedAnswer(index)
    setShowResult(true)
    if (index === step.correctIndex) {
      setScore((s) => s + 1)
    }
  }

  const handleNext = () => {
    if (currentStep < EXERCISE_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1)
      setSelectedAnswer(null)
      setShowResult(false)
    } else {
      setFinished(true)
    }
  }

  if (finished) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-6 text-center">
          <p className="text-2xl font-bold text-emerald-800">{score}/{EXERCISE_STEPS.length} correct</p>
          <p className="text-sm text-emerald-600 mt-2">
            {score === EXERCISE_STEPS.length
              ? 'You nailed it. You can combine datasets with formulas.'
              : 'Review the steps above and try the quiz below to reinforce the concepts.'}
          </p>
        </div>
        <button
          onClick={() => {
            setCurrentStep(0)
            setSelectedAnswer(null)
            setShowResult(false)
            setScore(0)
            setFinished(false)
          }}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">
          Step {currentStep + 1} of {EXERCISE_STEPS.length}
        </span>
        <div className="flex gap-1 flex-1">
          {EXERCISE_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                i <= currentStep ? 'bg-purple-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="space-y-4"
        >
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-purple-500 shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-purple-800">{step.question}</p>
            </div>
          </div>

          <div className="grid gap-2">
            {step.options.map((option, idx) => {
              let btnClass = 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
              if (showResult) {
                if (idx === step.correctIndex) {
                  btnClass = 'border-emerald-400 bg-emerald-100 text-emerald-800'
                } else if (idx === selectedAnswer && !isCorrect) {
                  btnClass = 'border-red-400 bg-red-100 text-red-800'
                } else {
                  btnClass = 'border-gray-200 bg-gray-50 text-gray-400'
                }
              }
              return (
                <button
                  key={idx}
                  onClick={() => !showResult && handleSelect(idx)}
                  disabled={showResult}
                  className={`rounded-lg border-2 px-4 py-3 text-sm font-medium text-left transition-all ${btnClass}`}
                >
                  <div className="flex items-center gap-2">
                    {showResult && idx === step.correctIndex && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    )}
                    {showResult && idx === selectedAnswer && !isCorrect && (
                      <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                    )}
                    <span className="font-mono text-xs">{option}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div className={`rounded-lg border p-4 ${
                isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
              }`}>
                <p className={`text-sm font-semibold ${isCorrect ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {isCorrect ? 'Correct!' : 'Not quite.'}
                </p>
                <p className={`text-sm mt-1 ${isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {step.explanation}
                </p>
              </div>
              <button
                onClick={handleNext}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
              >
                {currentStep < EXERCISE_STEPS.length - 1 ? 'Next Step' : 'See Results'}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/* ─── Main Module ─── */
export default function ModuleCombining() {
  return (
    <ModuleLayout moduleId={10}>
      <div className="space-y-8">
        {/* Section 0: Why Formulas */}
        <Section delay={0}>
          <WhyFormulasSection />
        </Section>

        {/* Section 1: The Problem */}
        <Section delay={0}>
          <ProblemSection />
        </Section>

        {/* Section 2: VLOOKUP with concatenated key */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Method 1: VLOOKUP with Concatenated Key</h2>
              <p className="text-lg text-gray-600">
                VLOOKUP can only search one column. Since our key is two fields (Offer ID + Date),
                we concatenate them with a separator into a single composite key.
              </p>
            </div>

            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-2">
              <p className="text-sm text-purple-800">
                <strong>The composite key trick:</strong>
              </p>
              <div className="rounded-lg bg-gray-900 p-3">
                <code className="text-sm text-emerald-400 font-mono">
                  =C2&amp;&quot;|&quot;&amp;A2 &rarr; &quot;offer_123|2024-01-15&quot;
                </code>
              </div>
              <p className="text-sm text-purple-700">
                Build the same key in both tables, and VLOOKUP can match on it.
              </p>
            </div>

            <VlookupDemo />
          </div>
        </Section>

        {/* Section 3: INDEX/MATCH */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Method 2: INDEX/MATCH with Composite Key</h2>
              <p className="text-lg text-gray-600">
                INDEX/MATCH separates the &quot;find&quot; step (MATCH) from the &quot;return&quot; step (INDEX).
                ARRAYFORMULA builds the composite key across the entire lookup column at once.
              </p>
            </div>

            <IndexMatchDemo />
          </div>
        </Section>

        {/* Section 4: QUERY approach */}
        <Section delay={0.1}>
          <QuerySection />
        </Section>

        {/* Section 5: Handling mismatches */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Handling Mismatches</h2>
              <p className="text-lg text-gray-600">
                Real data is messy. A row might exist in Clicks but not in Conversions (or vice versa).
                IFERROR keeps your formulas from breaking.
              </p>
            </div>

            <MismatchDemo />
          </div>
        </Section>

        {/* Section 6: Locking In Historical Data */}
        <Section delay={0.1}>
          <HistoricalDataSection />
        </Section>

        {/* Section 7: Build it yourself */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Build It Yourself</h2>
              <p className="text-lg text-gray-600">
                Walk through the steps of combining a real dataset. Pick the right approach at each stage.
              </p>
            </div>

            <BuildExercise />
          </div>
        </Section>

        {/* Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={10} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
